import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { NumericInput } from '../../components/NumericInput';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Loader2, Upload, X, Check, ChevronLeft, ChevronRight, Search, Plus, Image as ImageIcon, ClipboardPaste, ChevronDown, Save, FileText, Trash2, Download, UserCheck, Eye, Info, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, getCountryName } from '../../utils/countries';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { FormField, FormSelect, MultiSelectCheckboxes, ReviewSection, WizardStepper, ReviewProductHeader } from '../shared/WizardComponents';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useSubmittalsManager, commitSubmittals } from '../shared/useSubmittalsManager';
import { WizardSubmittalsStep, WizardSubmittalsReview } from '../shared/WizardSubmittalsStep';

// ===== 10-Step Wizard Config =====
const WIZARD_STEPS = [
  { id: 1, title: 'Product Information' },
  { id: 2, title: 'Insulation Types' },
  { id: 3, title: 'Material & Physical Properties' },
  { id: 4, title: 'Thermal Performance' },
  { id: 5, title: 'Fire, Smoke & Code Compliance' },
  { id: 6, title: 'Moisture & Vapor Properties' },
  { id: 7, title: 'Installation Parameters' },
  { id: 8, title: 'Interface & Responsibility' },
  { id: 9, title: 'Submittals' },
  { id: 10, title: 'Field Verification' },
  { id: 11, title: 'Review Summary' },
];
const TOTAL_STEPS = WIZARD_STEPS.length;

// ===== Static option lists =====
const INSULATION_GROUPS = [
  { group: 'Continuous Insulation (CI) \u2013 Exterior', items: ['Exterior Wall', 'Curtain Wall', 'Metal Stud'] },
  { group: 'Cavity Insulation (Interior / Stud Cavity)', items: ['Metal Stud Cavity Insulation', 'Batt / Semi-Rigid'] },
  { group: 'Roof Insulation', items: ['Tapered Insulation', 'Flat Roof Insulation', 'Cover Boards'] },
  { group: 'Below-Grade / Foundation Insulation', items: ['Foundation Wall Insulation', 'Slab Edge Insulation', 'Plaza/Podium Insulation'] },
  { group: 'Mechanical / Penetration-Related Insulation', items: ['Pipe Penetrations', 'Sleeve', 'Thermal Break Details'] },
  { group: 'Fire & Acoustic Insulation', items: ['Fire-Rated Assemblies', 'Sound Attenuation Batt (SAB)'] },
];
const MATERIAL_TYPES = ['Polyiso', 'Mineral Wool', 'XPS', 'Fiberglass', 'Other'];
const FACING_TYPES = ['Foil', 'Glass Mat', 'None'];
const TEST_STANDARDS = ['None', 'ASTM C518', 'ASTM C177'];
const FIRE_RATINGS = ['None', 'Class A', 'Class B', 'Class C'];
const ASTM_E84_OPTIONS = ['Flame Spread Index', 'Smoke Developed Index'];
const ASTM_E96_METHODS = ['None', 'A', 'B'];
const APPLICATION_AREAS = ['Below-grade', 'Exterior', 'Roof', 'Interior'];
const ATTACHMENT_METHODS = ['Mechanical', 'Adhesive', 'Loose-laid', 'Other'];
const SUBSTRATE_TYPES = ['Concrete', 'Metal Stud', 'Sheathing', 'Other'];
const ADJACENT_SYSTEMS = ['Air Barrier', 'WRB', 'Waterproofing', 'Curtain Wall'];
const INTERFACE_RESP_OPTIONS = ['This subcontractor', 'Other'];
const TRANSITION_MEMBRANE_OPTIONS = ['Yes', 'No'];
const DOCUMENT_TYPES = ['Product Data Sheet', 'Test Reports', 'SDS', 'Warranty Letter', 'Manufacturer Installation Guide'];

// ===== Initial form state =====
const INITIAL_FORM = {
  manufacturerId: '', productNameModel: '', countryCode: '',
  imageFile: null, imagePreview: null, insulationTypes: [],
  materialProperties: { materialType: '', materialTypeOther: '', thickness: '', density: '', compressiveStrength: '', facingType: '' },
  thermalPerformance: { declaredRValue: '', calculatedUValue: '', testStandard: '', temperatureCondition: '', cr: '' },
  fireCompliance: { fireRating: 'None', astmE84: '', nfpaReference: '', iccESReport: '' },
  moistureProperties: { waterAbsorption: '', vaporPermeance: '', astmE96Method: '', applicationArea: [] },
  installationParameters: { attachmentMethod: '', attachmentMethodOther: '', substrateType: '', maxFastenerSpacing: '', installationTempRange: '', uvExposureLimit: '' },
  interfaceResponsibility: { adjacentSystems: [], interfaceResponsibility: '', otherSubcontractor: '', transitionMembraneRequired: '' },
  fieldVerification: { installedThickness: '', nonConformance: '', inspectorComments: '', installationPhotos: [] },
  submittals: [],
};

// ===== Insulation helpers =====
const isItemSelected = (types, group, item) => { const g = (types || []).find(e => e.group === group); return g ? (g.items || g.types || []).includes(item) : false; };
const toggleItem = (types, group, item) => {
  const existing = (types || []).find(e => e.group === group);
  if (existing) {
    const items = existing.items || existing.types || [];
    const has = items.includes(item);
    if (has) { const ni = items.filter(i => i !== item); return ni.length === 0 ? types.filter(e => e.group !== group) : types.map(e => e.group === group ? { ...e, items: ni } : e); }
    return types.map(e => e.group === group ? { ...e, items: [...items, item] } : e);
  }
  return [...(types || []), { group, items: [item] }];
};
const getTotalSelected = (types) => (types || []).reduce((sum, g) => sum + (g.items || g.types || []).length, 0);

// ===== sessionStorage (v2 schema – reset old) =====
const STORAGE_PREFIX = 'specard_thermal_wizard_v2_';
const getStorageKey = (companyId, draftId) => `${STORAGE_PREFIX}${companyId}_${draftId || 'new'}`;
const saveWizardState = (companyId, draftId, state) => {
  try {
    const f = state.form;
    sessionStorage.setItem(getStorageKey(companyId, draftId), JSON.stringify({
      step: state.step, draftId: state.draftId,
      form: {
        manufacturerId: f.manufacturerId, productNameModel: f.productNameModel, countryCode: f.countryCode,
        imagePreview: f.imagePreview && !f.imagePreview.startsWith('blob:') ? f.imagePreview : null,
        insulationTypes: f.insulationTypes,
        materialProperties: f.materialProperties, thermalPerformance: f.thermalPerformance,
        fireCompliance: f.fireCompliance, moistureProperties: f.moistureProperties,
        installationParameters: f.installationParameters, interfaceResponsibility: f.interfaceResponsibility,
        fieldVerification: f.fieldVerification,
        submittals: f.submittals,
      },
    }));
  } catch { /* quota */ }
};
const loadWizardState = (companyId, draftId) => { try { const r = sessionStorage.getItem(getStorageKey(companyId, draftId)); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearWizardState = (companyId, draftId) => { try { sessionStorage.removeItem(getStorageKey(companyId, draftId)); if (draftId) sessionStorage.removeItem(getStorageKey(companyId, 'new')); } catch {} };

const mergeFormDefaults = (saved) => {
  if (!saved) return { ...INITIAL_FORM };
  // Migrate legacy applicationType → applicationArea
  const mp = saved.moistureProperties || {};
  if (mp.applicationType && !mp.applicationArea) {
    mp.applicationArea = [mp.applicationType];
    delete mp.applicationType;
  }
  return {
    ...INITIAL_FORM, ...saved,
    materialProperties: { ...INITIAL_FORM.materialProperties, ...(saved.materialProperties || {}) },
    thermalPerformance: { ...INITIAL_FORM.thermalPerformance, ...(saved.thermalPerformance || {}) },
    fireCompliance: { ...INITIAL_FORM.fireCompliance, ...(saved.fireCompliance || {}) },
    moistureProperties: { ...INITIAL_FORM.moistureProperties, ...mp, applicationArea: mp.applicationArea || saved.moistureProperties?.applicationArea || [] },
    installationParameters: { ...INITIAL_FORM.installationParameters, ...(saved.installationParameters || {}) },
    interfaceResponsibility: { ...INITIAL_FORM.interfaceResponsibility, ...(saved.interfaceResponsibility || {}), adjacentSystems: saved.interfaceResponsibility?.adjacentSystems || [] },
    fieldVerification: { ...INITIAL_FORM.fieldVerification, ...(saved.fieldVerification || {}), installationPhotos: saved.fieldVerification?.installationPhotos || [] },
    submittals: saved.submittals || [],
  };
};

// ===== Reusable mini-components (imported from shared/WizardComponents) =====

// ===== Photo Viewer Modal =====
const PhotoViewerModal = ({ photos, currentIndex, onIndexChange, onClose }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onIndexChange(prev => prev > 0 ? prev - 1 : photos.length - 1);
      if (e.key === 'ArrowRight') onIndexChange(prev => prev < photos.length - 1 ? prev + 1 : 0);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [photos.length, onClose, onIndexChange]);

  const photo = photos[currentIndex];
  if (!photo) return null;

  return (
    <div className="absolute inset-0 z-[60] flex items-center justify-center rounded-lg overflow-hidden" data-testid="fv-photo-viewer-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Navigation arrows — fixed to overlay center */}
      {photos.length > 1 && (
        <>
          <button className="absolute left-3 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-slate-100 text-slate-700 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border transition-colors"
            onClick={() => onIndexChange(currentIndex > 0 ? currentIndex - 1 : photos.length - 1)}
            data-testid="fv-viewer-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button className="absolute right-3 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-slate-100 text-slate-700 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border transition-colors"
            onClick={() => onIndexChange(currentIndex < photos.length - 1 ? currentIndex + 1 : 0)}
            data-testid="fv-viewer-next"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      <div className="relative z-[65] flex flex-col items-center bg-white rounded-xl shadow-2xl p-4 max-w-[85%] max-h-[90%]">
        {/* Close */}
        <button className="absolute -top-3 -right-3 bg-slate-700 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-slate-900 z-20"
          onClick={onClose} data-testid="fv-viewer-close"><X className="h-4 w-4" /></button>
        {/* Image */}
        <img src={photo.url} alt={photo.fileName} className="max-w-full max-h-[70%] rounded-lg object-contain" data-testid="fv-viewer-image" />
        {/* Footer */}
        <div className="mt-3 text-center">
          <p className="text-sm text-slate-800 font-medium">{photo.fileName}</p>
          <p className="text-xs text-slate-400">{currentIndex + 1} / {photos.length}</p>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN COMPONENT =====
export const AddProductWizard = ({ open, onOpenChange, onComplete, onRefresh, editProduct, companyId, projectId, csiItemId }) => {
  const isEdit = !!editProduct;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const initialFormRef = useRef(INITIAL_FORM);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteUploading, setPasteUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(null);
  const { startDownload, cancelDownload, getState: getDlState } = useDownload();

  // Manufacturer state
  const [manufacturers, setManufacturers] = useState([]);
  const [mfrSearch, setMfrSearch] = useState('');
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [creatingMfr, setCreatingMfr] = useState(false);
  const mfrRef = useRef(null);

  // Country state
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  // Insulation groups expand
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(INSULATION_GROUPS.map(g => g.group)));
  const rightPanelRef = useRef(null);
  const lastAddedItemRef = useRef(null);
  const leftPanelRef = useRef(null);
  const leftScrollRef = useRef(0);

  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const fileInputRef = useRef(null);
  const initializedRef = useRef(false);
  const submittals = useSubmittalsManager();

  // ===== Restore from sessionStorage =====
  useEffect(() => {
    if (!open || initializedRef.current) return;
    initializedRef.current = true;
    if (editProduct) {
      const editForm = mergeFormDefaults({
        manufacturerId: editProduct.manufacturerId || '', productNameModel: editProduct.productNameModel || '',
        countryCode: editProduct.countryCode || '', imageFile: null,
        imagePreview: editProduct.imageUrl || editProduct.imageCloudinary?.urls?.medium || null,
        insulationTypes: Array.isArray(editProduct.insulationTypes) ? editProduct.insulationTypes.map(g => ({ group: g.group, items: g.items || g.types || [] })) : [],
        materialProperties: editProduct.materialProperties, thermalPerformance: editProduct.thermalPerformance,
        fireCompliance: editProduct.fireCompliance, moistureProperties: editProduct.moistureProperties,
        installationParameters: editProduct.installationParameters, interfaceResponsibility: editProduct.interfaceResponsibility,
        fieldVerification: editProduct.fieldVerification,
        submittals: editProduct.submittals || [],
        qrToken: editProduct.qrToken,
      });
      setForm(editForm);
      initialFormRef.current = mergeFormDefaults({ ...editForm, qrToken: undefined });
      setDraftId(editProduct.id);
      submittals.init(editProduct.submittals || []);
      setStep(1);
    } else {
      clearWizardState(companyId, 'new');
      setForm({ ...INITIAL_FORM }); setStep(1); setDraftId(null); initialFormRef.current = { ...INITIAL_FORM };
      submittals.init([]);
    }
  }, [open, editProduct, companyId]);

  // Persist
  useEffect(() => {
    if (!open || !initializedRef.current) return;
    saveWizardState(companyId, draftId || (isEdit ? editProduct?.id : 'new'), { step, draftId, form });
  }, [step, form, draftId, open, companyId, isEdit, editProduct]);

  // Dirty check
  const isDirty = (() => {
    if (submittals.isDirty) return true;
    const i = initialFormRef.current;
    return form.productNameModel !== i.productNameModel || form.manufacturerId !== i.manufacturerId ||
      form.countryCode !== i.countryCode || !!form.imageFile ||
      JSON.stringify(form.insulationTypes) !== JSON.stringify(i.insulationTypes) ||
      JSON.stringify(form.materialProperties) !== JSON.stringify(i.materialProperties) ||
      JSON.stringify(form.thermalPerformance) !== JSON.stringify(i.thermalPerformance) ||
      JSON.stringify(form.fireCompliance) !== JSON.stringify(i.fireCompliance) ||
      JSON.stringify(form.moistureProperties) !== JSON.stringify(i.moistureProperties) ||
      JSON.stringify(form.installationParameters) !== JSON.stringify(i.installationParameters) ||
      JSON.stringify(form.interfaceResponsibility) !== JSON.stringify(i.interfaceResponsibility) ||
      JSON.stringify(form.fieldVerification) !== JSON.stringify(i.fieldVerification);
  })();

  // Fetch manufacturers
  const fetchManufacturers = useCallback(async () => { try { const res = await axios.get(`${API}/thermal-insulation-manufacturers`); setManufacturers(res.data); } catch {} }, []);
  useEffect(() => { fetchManufacturers(); }, [fetchManufacturers]);

  // Outside click dropdowns
  useEffect(() => {
    const h = (e) => { if (mfrRef.current && !mfrRef.current.contains(e.target)) setMfrDropdownOpen(false); if (countryRef.current && !countryRef.current.contains(e.target)) setCountryDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectedManufacturer = manufacturers.find(m => m.id === form.manufacturerId);
  const filteredManufacturers = manufacturers.filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase()));
  const showAddNew = mfrSearch.trim() && !filteredManufacturers.some(m => m.name.toLowerCase() === mfrSearch.trim().toLowerCase());
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountry = COUNTRIES.find(c => c.code === form.countryCode);
  const totalSelected = getTotalSelected(form.insulationTypes);
  const selectedGroupsForPanel = INSULATION_GROUPS.map(g => ({ group: g.group, items: g.items.filter(i => isItemSelected(form.insulationTypes, g.group, i)) })).filter(g => g.items.length > 0);

  // Restore left panel scroll BEFORE browser paint (sync after DOM mutation)
  useLayoutEffect(() => {
    if (leftPanelRef.current) leftPanelRef.current.scrollTop = leftScrollRef.current;
  }, [form.insulationTypes]);

  // After toggle: scroll+highlight right panel
  useEffect(() => {
    const info = lastAddedItemRef.current;
    if (!info || !rightPanelRef.current) return;
    lastAddedItemRef.current = null;

    requestAnimationFrame(() => {
      const container = rightPanelRef.current;
      if (!container) return;

      // For 'add', find the newly rendered item; for 'remove', skip scroll
      if (info.action === 'remove') return;

      const el = container.querySelector(`[data-insulation-key="${info.key}"]`);
      if (!el) return;

      // Check visibility
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      const inView = eRect.top >= cRect.top && eRect.bottom <= cRect.bottom;

      const applyHighlight = () => {
        el.style.transition = 'background-color 200ms ease-out';
        el.style.backgroundColor = 'rgb(253 186 116)'; // orange-300
        setTimeout(() => { el.style.backgroundColor = ''; }, 250);
      };

      if (!inView) {
        const targetTop = el.offsetTop - container.offsetTop - cRect.height / 3;
        container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        setTimeout(applyHighlight, 300); // after scroll settles
      } else {
        applyHighlight();
      }
    });
  }, [form.insulationTypes]);
  const isReviewStep = step === TOTAL_STEPS;
  const isBelowGradeOnly = form.moistureProperties.applicationArea?.length === 1 && form.moistureProperties.applicationArea[0] === 'Below-grade';

  // Form helpers
  const updateNested = (section, field, value) => { setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } })); setErrors(prev => ({ ...prev, [`${section}.${field}`]: null })); };
  const isFireRatingNone = form.fireCompliance.fireRating === 'None' || !form.fireCompliance.fireRating;

  // Numeric-only validation helper (allows empty)
  const isNumericValue = (v) => !v || /^-?\d*\.?\d*$/.test(v.trim());
  // Fraction/decimal validation helper (allows 12, 0.4, 1/4 — rejects abc, 1/, 1-2)
  const isFractionOrDecimal = (v) => !v || /^(\d+(\.\d+)?|\d+\/\d+)$/.test(v.trim());

  // Smart: Clear UV when Below-grade is the ONLY selection
  useEffect(() => { if (isBelowGradeOnly) setForm(prev => ({ ...prev, installationParameters: { ...prev.installationParameters, uvExposureLimit: '' } })); }, [isBelowGradeOnly]);

  // Fraction/decimal input filter — allows digits, dot, slash only
  const handleFractionInput = (section, field) => (e) => {
    const v = e.target.value;
    if (v === '' || /^[\d./]*$/.test(v)) updateNested(section, field, v);
  };

  // Smart: Clear fire compliance fields when fireRating is None
  // Inner prev check guards against stale-closure race with init effect
  useEffect(() => {
    if (isFireRatingNone) {
      setForm(prev => {
        const prevNone = prev.fireCompliance.fireRating === 'None' || !prev.fireCompliance.fireRating;
        if (prevNone && (prev.fireCompliance.astmE84 || prev.fireCompliance.nfpaReference || prev.fireCompliance.iccESReport)) {
          return { ...prev, fireCompliance: { ...prev.fireCompliance, astmE84: '', nfpaReference: '', iccESReport: '' } };
        }
        return prev;
      });
    }
  }, [isFireRatingNone]);

  // Smart: Clear materialTypeOther when not Other
  // Inner prev check guards against stale-closure race with init effect
  useEffect(() => {
    if (form.materialProperties.materialType !== 'Other') {
      setForm(prev => {
        if (prev.materialProperties.materialType !== 'Other' && prev.materialProperties.materialTypeOther) {
          return { ...prev, materialProperties: { ...prev.materialProperties, materialTypeOther: '' } };
        }
        return prev;
      });
    }
  }, [form.materialProperties.materialType]);

  // Smart: Clear attachmentMethodOther when not Other
  useEffect(() => {
    if (form.installationParameters.attachmentMethod !== 'Other') {
      setForm(prev => {
        if (prev.installationParameters.attachmentMethod !== 'Other' && prev.installationParameters.attachmentMethodOther) {
          return { ...prev, installationParameters: { ...prev.installationParameters, attachmentMethodOther: '' } };
        }
        return prev;
      });
    }
  }, [form.installationParameters.attachmentMethod]);

  // ===== Step 1 Handlers =====
  const handleCreateManufacturer = async () => {
    const name = mfrSearch.trim(); if (!name) return;
    setCreatingMfr(true);
    try { const res = await axios.post(`${API}/thermal-insulation-manufacturers`, { name }); setManufacturers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name))); setForm(prev => ({ ...prev, manufacturerId: res.data.id })); setMfrSearch(''); setMfrDropdownOpen(false); toast.success(`Manufacturer "${name}" created`); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to create manufacturer'); }
    finally { setCreatingMfr(false); }
  };
  const handleSelectManufacturer = (m) => { setForm(prev => ({ ...prev, manufacturerId: m.id })); setMfrSearch(''); setMfrDropdownOpen(false); };
  const handleSelectCountry = (c) => { setForm(prev => ({ ...prev, countryCode: c.code })); setCountrySearch(''); setCountryDropdownOpen(false); };
  const handleToggleInsulationItem = (group, item) => {
    // Snapshot left panel scroll so we can restore after render
    if (leftPanelRef.current) leftScrollRef.current = leftPanelRef.current.scrollTop;

    const wasSelected = isItemSelected(form.insulationTypes, group, item);
    lastAddedItemRef.current = { key: `${group}::${item}`, action: wasSelected ? 'remove' : 'add' };

    // Focus right panel first, then toggle via microtask
    rightPanelRef.current?.focus({ preventScroll: true });
    Promise.resolve().then(() => {
      setForm(prev => ({ ...prev, insulationTypes: toggleItem(prev.insulationTypes, group, item) }));
    });
  };
  const toggleGroupExpand = (group) => { setExpandedGroups(prev => { const n = new Set(prev); n.has(group) ? n.delete(group) : n.add(group); return n; }); };

  const applyImageFile = (file) => {
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) { toast.error('Only PNG, JPEG, and WebP images are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    if (form.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(form.imagePreview);
    setForm(prev => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };
  const handleImageSelect = (e) => { if (e.target.files[0]) applyImageFile(e.target.files[0]); };
  const handlePasteUpload = async (f) => { setPasteUploading(true); try { applyImageFile(f); setShowPasteModal(false); } finally { setPasteUploading(false); } };
  const handleRemoveImage = () => { if (form.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(form.imagePreview); setForm(prev => ({ ...prev, imageFile: null, imagePreview: null })); if (fileInputRef.current) fileInputRef.current.value = ''; };

  // ===== Submittal Handlers =====
  const handlePreviewSubmittal = async (submittal) => {
    if (!draftId || submittal._status === 'pending') return;
    try {
      const res = await axios.get(`${API}/thermal-insulation-products/${projectId}/${draftId}/submittals/${submittal.id}/preview-url`);
      setPreviewSubmittal({ ...submittal, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  // ===== Validation =====
  const validateDraft = () => {
    const e = {};
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    setErrors(e); return !Object.keys(e).length;
  };
  const validateAllSteps = () => {
    const e = {};
    // Step 1
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    if (!form.manufacturerId) e.manufacturerId = 'Manufacturer is required';
    // Step 2
    if (getTotalSelected(form.insulationTypes) === 0) e.insulationTypes = 'Select at least one insulation type';
    // Step 3
    if (!form.materialProperties.materialType) e['materialProperties.materialType'] = 'Material Type is required';
    if (form.materialProperties.materialType === 'Other' && !form.materialProperties.materialTypeOther?.trim()) e['materialProperties.materialTypeOther'] = 'Please specify material type';
    if (!form.materialProperties.thickness) e['materialProperties.thickness'] = 'Thickness is required';
    if (form.materialProperties.thickness && !isFractionOrDecimal(form.materialProperties.thickness)) e['materialProperties.thickness'] = 'Enter a valid number or fraction (e.g. 0.4, 1/4)';
    if (form.materialProperties.density && !isFractionOrDecimal(form.materialProperties.density)) e['materialProperties.density'] = 'Enter a valid number or fraction';
    if (form.materialProperties.compressiveStrength && !isFractionOrDecimal(form.materialProperties.compressiveStrength)) e['materialProperties.compressiveStrength'] = 'Enter a valid number or fraction';
    // Step 4
    if (!form.thermalPerformance.declaredRValue) e['thermalPerformance.declaredRValue'] = 'R-Value is required';
    if (form.thermalPerformance.declaredRValue && !isNumericValue(form.thermalPerformance.declaredRValue)) e['thermalPerformance.declaredRValue'] = 'Must be numeric';
    if (form.thermalPerformance.calculatedUValue && !isNumericValue(form.thermalPerformance.calculatedUValue)) e['thermalPerformance.calculatedUValue'] = 'Must be numeric';
    if (form.thermalPerformance.temperatureCondition && !isNumericValue(form.thermalPerformance.temperatureCondition)) e['thermalPerformance.temperatureCondition'] = 'Must be numeric';
    if (form.thermalPerformance.cr && !isNumericValue(form.thermalPerformance.cr)) e['thermalPerformance.cr'] = 'Must be numeric';
    // Step 5 — Fire Rating is NOT required (None is valid); but if not None, ASTM E84 + NFPA required
    const fireNone = form.fireCompliance.fireRating === 'None' || !form.fireCompliance.fireRating;
    if (!fireNone) {
      if (!form.fireCompliance.astmE84) e['fireCompliance.astmE84'] = 'ASTM E84 is required when Fire Rating is set';
      if (!form.fireCompliance.nfpaReference?.trim()) e['fireCompliance.nfpaReference'] = 'NFPA / UL Reference is required when Fire Rating is set';
    }
    // Step 6
    if (!form.moistureProperties.applicationArea?.length) e['moistureProperties.applicationArea'] = 'At least one Application Area is required';
    if (form.moistureProperties.waterAbsorption && !isNumericValue(form.moistureProperties.waterAbsorption)) e['moistureProperties.waterAbsorption'] = 'Must be numeric';
    if (form.moistureProperties.vaporPermeance && !isNumericValue(form.moistureProperties.vaporPermeance)) e['moistureProperties.vaporPermeance'] = 'Must be numeric';
    // Step 7
    if (!form.installationParameters.attachmentMethod) e['installationParameters.attachmentMethod'] = 'Attachment Method is required';
    if (form.installationParameters.uvExposureLimit && !isNumericValue(form.installationParameters.uvExposureLimit)) e['installationParameters.uvExposureLimit'] = 'Must be numeric';
    // Step 8
    if (!form.interfaceResponsibility.interfaceResponsibility) e['interfaceResponsibility.interfaceResponsibility'] = 'Interface Responsibility is required';
    if (form.interfaceResponsibility.interfaceResponsibility === 'Other' && !form.interfaceResponsibility.otherSubcontractor?.trim()) e['interfaceResponsibility.otherSubcontractor'] = 'Please specify the subcontractor';
    if (!form.interfaceResponsibility.transitionMembraneRequired) e['interfaceResponsibility.transitionMembraneRequired'] = 'Transition Membrane Required is required';
    // Step 9
    // Step 9 — Submittals (optional)
    // Step 10 — Field Verification
    if (form.fieldVerification.nonConformance === 'Yes' && !form.fieldVerification.inspectorComments?.trim()) {
      e['fieldVerification.inspectorComments'] = 'Inspector Comments are required when Non-conformance is detected';
    }
    setErrors(e);
    return e;
  };
  const getFirstErrorStep = (e) => {
    if (['productNameModel', 'manufacturerId', 'countryCode', 'imageFile'].some(f => e[f])) return 1;
    if (e.insulationTypes) return 2;
    if (Object.keys(e).some(k => k.startsWith('materialProperties'))) return 3;
    if (Object.keys(e).some(k => k.startsWith('thermalPerformance'))) return 4;
    if (Object.keys(e).some(k => k.startsWith('fireCompliance'))) return 5;
    if (Object.keys(e).some(k => k.startsWith('moistureProperties'))) return 6;
    if (Object.keys(e).some(k => k.startsWith('installationParameters'))) return 7;
    if (Object.keys(e).some(k => k.startsWith('interfaceResponsibility'))) return 8;
    if (e.submittals) return 9;
    if (Object.keys(e).some(k => k.startsWith('fieldVerification'))) return 10;
    return 1;
  };

  // Build payload
  const buildStepPayload = () => {
    const p = {};
    const mp = form.materialProperties; if (mp.materialType || mp.materialTypeOther || mp.thickness || mp.density || mp.compressiveStrength || mp.facingType) p.materialProperties = mp;
    const tp = form.thermalPerformance; if (tp.declaredRValue || tp.calculatedUValue || tp.testStandard || tp.temperatureCondition || tp.cr) p.thermalPerformance = tp;
    const fc = form.fireCompliance; if (fc.fireRating || fc.astmE84 || fc.nfpaReference || fc.iccESReport) p.fireCompliance = fc;
    const mo = form.moistureProperties; if (mo.waterAbsorption || mo.vaporPermeance || mo.astmE96Method || mo.applicationArea?.length) p.moistureProperties = mo;
    const ip = form.installationParameters; if (ip.attachmentMethod || ip.attachmentMethodOther || ip.substrateType || ip.maxFastenerSpacing || ip.installationTempRange || ip.uvExposureLimit) p.installationParameters = ip;
    const ir = form.interfaceResponsibility; if (ir.adjacentSystems?.length > 0 || ir.interfaceResponsibility || ir.transitionMembraneRequired) p.interfaceResponsibility = ir;
    const fv = form.fieldVerification; if (fv.installedThickness || fv.nonConformance || fv.inspectorComments || fv.installationPhotos?.length) p.fieldVerification = fv;
    return p;
  };

  // ===== Save Draft =====
  const handleSaveDraft = async () => {
    if (!validateDraft()) { toast.error('Please fill in required fields on Step 1 before saving'); setStep(1); return; }
    setSaving(true);
    try {
      let pid = draftId;
      const payload = { productNameModel: form.productNameModel.trim(), status: 'draft', csiItemId, ...buildStepPayload() };
      if (form.manufacturerId) payload.manufacturerId = form.manufacturerId;
      if (form.countryCode) payload.countryCode = form.countryCode;
      if (form.insulationTypes.length > 0) payload.insulationTypes = form.insulationTypes;
      if (!pid) { const res = await axios.post(`${API}/thermal-insulation-products/${projectId}`, payload); pid = res.data.id; setDraftId(pid); setForm(prev => ({ ...prev, qrToken: res.data.qrToken })); clearWizardState(companyId, 'new'); }
      else { await axios.patch(`${API}/thermal-insulation-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        const imgRes = await axios.post(`${API}/thermal-insulation-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = imgRes.data?.imageUrl || imgRes.data?.imageCloudinary?.urls?.medium;
        if (url) setForm(prev => ({ ...prev, imageFile: null, imagePreview: url }));
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'thermal-insulation-products', axios, API);
        const fresh = await axios.get(`${API}/thermal-insulation-products/${projectId}/${pid}`);
        submittals.init(fresh.data?.submittals || []);
      }
      saveWizardState(companyId, pid, { step, draftId: pid, form: { ...form, imageFile: null } });
      initialFormRef.current = { ...form, imageFile: null };
      toast.success('Draft saved'); if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save draft'); }
    finally { setSaving(false); }
  };

  // ===== Final Save (only from Review) =====
  const handleFinalSave = async () => {
    const validationErrors = validateAllSteps();
    if (Object.keys(validationErrors).length > 0) {
      const targetStep = getFirstErrorStep(validationErrors);
      if (targetStep !== step) setStep(targetStep);
      toast.error('Please complete all required fields before finalizing');
      return;
    }
    setFinalizing(true);
    try {
      let pid = draftId;
      const payload = { manufacturerId: form.manufacturerId, productNameModel: form.productNameModel.trim(), countryCode: form.countryCode, insulationTypes: form.insulationTypes, status: 'active', csiItemId, ...buildStepPayload() };
      if (!pid) { const res = await axios.post(`${API}/thermal-insulation-products/${projectId}`, payload); pid = res.data.id; }
      else { await axios.patch(`${API}/thermal-insulation-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) { const fd = new FormData(); fd.append('file', form.imageFile); await axios.post(`${API}/thermal-insulation-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'thermal-insulation-products', axios, API);
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      clearWizardState(companyId, pid); clearWizardState(companyId, 'new');
      if (onComplete) onComplete(); resetAndClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save product'); }
    finally { setFinalizing(false); }
  };

  // Navigation
  const handleNext = () => setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

  const resetAndClose = () => {
    if (draftId) clearWizardState(companyId, draftId); clearWizardState(companyId, 'new');
    setStep(1); setForm(INITIAL_FORM); initialFormRef.current = INITIAL_FORM;
    setErrors({}); setDraftId(null); setMfrSearch(''); setCountrySearch('');
    setExpandedGroups(new Set(INSULATION_GROUPS.map(g => g.group)));
    setShowCloseConfirm(false); initializedRef.current = false;
    submittals.rollback();
    onOpenChange(false);
  };
  const requestClose = () => { if (isDirty) setShowCloseConfirm(true); else resetAndClose(); };

  // ===== RENDER =====
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent className="max-w-4xl p-0 flex flex-col" style={{ height: '75vh', maxHeight: '85vh', minHeight: '400px' }} data-testid="add-product-wizard">
        {/* Fixed Header */}
        <div className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>Step {step} of {TOTAL_STEPS}: {WIZARD_STEPS[step - 1]?.title}</DialogDescription>
          </DialogHeader>
          <WizardStepper steps={WIZARD_STEPS} currentStep={step} onStepClick={setStep} />
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="wizard-body">

          {/* === STEP 1: Product Information === */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Product Information</h4>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageSelect} data-testid="image-file-input" />
              <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-stretch" data-testid="product-info-grid">
                {/* Image */}
                <div className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center relative overflow-hidden bg-slate-50 transition-colors min-h-[220px] ${errors.imageFile ? 'border-red-400 bg-red-50/30' : 'border-slate-300'}`} data-testid="image-upload-area">
                  {form.imagePreview ? (
                    <><img src={form.imagePreview} alt="Preview" className="w-full h-full object-contain p-2" data-testid="image-preview" onError={(e) => { e.target.style.display = 'none'; }} /><button type="button" className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 z-10" onClick={handleRemoveImage} data-testid="remove-image-btn"><X className="h-3 w-3" /></button></>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center"><ImageIcon className="h-10 w-10 text-slate-300" /><p className="text-xs text-slate-400">Product Image</p><p className="text-[10px] text-slate-400">PNG, JPG, WebP (max 5MB)</p></div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-slate-200 flex items-center justify-center gap-1.5 py-1.5 px-2">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowPasteModal(true)} data-testid="paste-from-clipboard-btn"><ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste</Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => fileInputRef.current?.click()} data-testid="upload-image-btn"><Upload className="h-3.5 w-3.5 mr-1" />{form.imagePreview ? 'Change' : 'Upload'}</Button>
                  </div>
                  {errors.imageFile && <p className="absolute top-1 left-2 text-[10px] text-red-500 bg-white/80 px-1 rounded">{errors.imageFile}</p>}
                </div>
                {/* Fields */}
                <div className="space-y-3">
                  {/* Manufacturer dropdown */}
                  <div className="space-y-1">
                    <Label className="text-sm">Manufacturer *</Label>
                    <div className="relative" ref={mfrRef}>
                      <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.manufacturerId ? 'border-red-500' : 'border-input'}`} onClick={() => setMfrDropdownOpen(true)} data-testid="manufacturer-select">
                        {selectedManufacturer ? (<div className="flex items-center justify-between w-full"><span className="text-sm">{selectedManufacturer.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, manufacturerId: '' })); }} /></div>) : (<span className="text-muted-foreground text-sm">Select manufacturer...</span>)}
                      </div>
                      {mfrDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search or add new..." value={mfrSearch} onChange={(e) => setMfrSearch(e.target.value)} autoFocus data-testid="manufacturer-search-input" /></div></div>
                          <div className="py-1">
                            {filteredManufacturers.map((m) => (<div key={m.id} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${m.id === form.manufacturerId ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectManufacturer(m)} data-testid={`manufacturer-option-${m.id}`}>{m.id === form.manufacturerId && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={m.id === form.manufacturerId ? '' : 'ml-6'}>{m.name}</span>{m.isMine && <UserCheck className="h-3.5 w-3.5 ml-auto text-orange-400 shrink-0" title="Added by you" />}</div>))}
                            {showAddNew && (<div className="flex items-center px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t" onClick={handleCreateManufacturer} data-testid="add-new-manufacturer-btn">{creatingMfr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add new: &quot;{mfrSearch.trim()}&quot;</div>)}
                            {filteredManufacturers.length === 0 && !showAddNew && (<div className="px-3 py-2 text-sm text-slate-400">No manufacturers found</div>)}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.manufacturerId && <p className="text-xs text-red-500">{errors.manufacturerId}</p>}
                  </div>
                  <FormField label="Product Name / Model" required error={errors.productNameModel}>
                    <Input placeholder="e.g., ThermoShield 3000" value={form.productNameModel} onChange={(e) => { setForm(prev => ({ ...prev, productNameModel: e.target.value })); if (e.target.value.trim()) setErrors(prev => ({ ...prev, productNameModel: null })); }} className={errors.productNameModel ? 'border-red-500' : ''} data-testid="product-name-input" />
                  </FormField>
                  {/* Country dropdown */}
                  <div className="space-y-1">
                    <Label className="text-sm">Country of Manufacturer</Label>
                    <div className="relative" ref={countryRef}>
                      <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.countryCode ? 'border-red-500' : 'border-input'}`} onClick={() => setCountryDropdownOpen(true)} data-testid="country-select">
                        {selectedCountry ? (<div className="flex items-center justify-between w-full"><span className="text-sm">{selectedCountry.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, countryCode: '' })); }} /></div>) : (<span className="text-muted-foreground text-sm">Select country...</span>)}
                      </div>
                      {countryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} autoFocus data-testid="country-search-input" /></div></div>
                          <div className="py-1">{filteredCountries.slice(0, 50).map((c) => (<div key={c.code} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${c.code === form.countryCode ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectCountry(c)} data-testid={`country-option-${c.code}`}>{c.code === form.countryCode && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={c.code === form.countryCode ? '' : 'ml-6'}>{c.name}</span></div>))}</div>
                        </div>
                      )}
                    </div>
                    {errors.countryCode && <p className="text-xs text-red-500">{errors.countryCode}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === STEP 2: Insulation Types === */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <h4 className="font-semibold text-sm border-b pb-2">Insulation Types{totalSelected > 0 && <span className="ml-2 text-xs font-normal text-slate-500">({totalSelected} selected)</span>}</h4>
              {errors.insulationTypes && <p className="text-xs text-red-500 -mt-1">{errors.insulationTypes}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insulation-types-section">
                <div className="border rounded-lg overflow-hidden" data-testid="insulation-left-panel">
                  <div className="px-3 py-2 bg-slate-100 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Insulation Types</p></div>
                  <div className="max-h-[400px] overflow-y-auto" ref={leftPanelRef}>
                    {INSULATION_GROUPS.map((g) => {
                      const isExpanded = expandedGroups.has(g.group);
                      return (
                        <div key={g.group}>
                          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => toggleGroupExpand(g.group)}>
                            <span className="text-sm font-medium text-slate-800">{g.group}</span>
                            <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                          </div>
                          {isExpanded && g.items.map((item) => {
                            const checked = isItemSelected(form.insulationTypes, g.group, item);
                            return (
                              <div key={item} role="button"
                                className={`flex items-center gap-2.5 px-3 py-2 pl-6 border-b last:border-b-0 cursor-pointer select-none transition-colors text-sm ${checked ? 'bg-orange-50' : 'hover:bg-slate-200'}`}
                                onClick={() => handleToggleInsulationItem(g.group, item)}
                                onMouseDown={(e) => e.preventDefault()}
                                data-testid={`insulation-item-${g.group}-${item}`}
                              >
                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${checked ? 'bg-orange-500 border-orange-500' : 'border-slate-300 bg-white'}`}>{checked && <Check className="h-3 w-3 text-white" />}</div>
                                <span className={checked ? 'text-slate-900' : 'text-slate-600'}>{item}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden" data-testid="insulation-right-panel">
                  <div className="px-3 py-2 bg-slate-100 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Selected Insulation</p></div>
                  <div className="max-h-[400px] overflow-y-auto p-3 outline-none" ref={rightPanelRef} tabIndex={-1}>
                    {selectedGroupsForPanel.length === 0 ? (<p className="text-sm text-slate-400 text-center py-6">No insulation selected</p>) : (
                      <div className="space-y-3">{selectedGroupsForPanel.map((sg) => (
                        <div key={sg.group}><p className="text-xs font-semibold text-slate-700 mb-1">- {sg.group}</p><div className="space-y-0.5 ml-2">{sg.items.map((item) => (
                          <div key={item}
                            className="flex items-center gap-2 cursor-pointer group text-sm animate-[fadeSlideIn_200ms_ease-out]"
                            onClick={() => handleToggleInsulationItem(sg.group, item)}
                            onMouseDown={(e) => e.preventDefault()}
                            role="button"
                            data-insulation-key={`${sg.group}::${item}`}
                          >
                            <div className="w-3.5 h-3.5 rounded border bg-orange-500 border-orange-500 flex-shrink-0 flex items-center justify-center"><Check className="h-2.5 w-2.5 text-white" /></div>
                            <span className="text-slate-700 group-hover:text-slate-900">&gt; {item}</span>
                          </div>
                        ))}</div></div>
                      ))}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === STEP 3: Material & Physical Properties === */}
          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <h4 className="font-semibold text-sm border-b pb-2">Material & Physical Properties</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Material Type" required error={errors['materialProperties.materialType']}><FormSelect value={form.materialProperties.materialType} onValueChange={(v) => updateNested('materialProperties', 'materialType', v)} placeholder="Select material type..." options={MATERIAL_TYPES} error={errors['materialProperties.materialType']} testId="material-type-select" /></FormField>
                {form.materialProperties.materialType === 'Other' ? (
                  <FormField label="Specify Material Type" required error={errors['materialProperties.materialTypeOther']}><Input placeholder="Enter custom material type..." value={form.materialProperties.materialTypeOther} onChange={(e) => updateNested('materialProperties', 'materialTypeOther', e.target.value)} className={errors['materialProperties.materialTypeOther'] ? 'border-red-500' : ''} data-testid="material-type-other-input" /></FormField>
                ) : (<div />)}
                <FormField label="Thickness (in)" required error={errors['materialProperties.thickness']}><Input placeholder="e.g., 2.0 or 1/4" value={form.materialProperties.thickness} onChange={handleFractionInput('materialProperties', 'thickness')} className={errors['materialProperties.thickness'] ? 'border-red-500' : ''} data-testid="thickness-input" /></FormField>
                <FormField label="Density (pcf / kg/m³)" error={errors['materialProperties.density']}><Input placeholder="e.g., 2.0 or 1/4" value={form.materialProperties.density} onChange={handleFractionInput('materialProperties', 'density')} className={errors['materialProperties.density'] ? 'border-red-500' : ''} data-testid="density-input" /></FormField>
                <FormField label="Compressive Strength (psi)" error={errors['materialProperties.compressiveStrength']}><Input placeholder="e.g., 25 or 1/4" value={form.materialProperties.compressiveStrength} onChange={handleFractionInput('materialProperties', 'compressiveStrength')} className={errors['materialProperties.compressiveStrength'] ? 'border-red-500' : ''} data-testid="compressive-strength-input" /></FormField>
                <FormField label="Facing Type"><FormSelect value={form.materialProperties.facingType} onValueChange={(v) => updateNested('materialProperties', 'facingType', v)} placeholder="Select facing type..." options={FACING_TYPES} testId="facing-type-select" /></FormField>
              </div>
            </div>
          )}

          {/* === STEP 4: Thermal Performance === */}
          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-step-4">
              <h4 className="font-semibold text-sm border-b pb-2">Thermal Performance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="R-Value" required error={errors['thermalPerformance.declaredRValue']}><NumericInput placeholder="e.g., 13.0" value={form.thermalPerformance.declaredRValue} onChange={(v) => updateNested('thermalPerformance', 'declaredRValue', v)} className={errors['thermalPerformance.declaredRValue'] ? 'border-red-500' : ''} data-testid="r-value-input" /></FormField>
                <FormField label="U-Value" error={errors['thermalPerformance.calculatedUValue']}><NumericInput placeholder="e.g., 0.077" value={form.thermalPerformance.calculatedUValue} onChange={(v) => updateNested('thermalPerformance', 'calculatedUValue', v)} className={errors['thermalPerformance.calculatedUValue'] ? 'border-red-500' : ''} data-testid="u-value-input" /></FormField>
                <FormField label="Test Standard"><FormSelect value={form.thermalPerformance.testStandard} onValueChange={(v) => updateNested('thermalPerformance', 'testStandard', v)} placeholder="Select test standard..." options={TEST_STANDARDS} testId="test-standard-select" /></FormField>
                <FormField label="Temperature Condition (°F)" error={errors['thermalPerformance.temperatureCondition']}><NumericInput placeholder="e.g., 75" value={form.thermalPerformance.temperatureCondition} onChange={(v) => updateNested('thermalPerformance', 'temperatureCondition', v)} className={errors['thermalPerformance.temperatureCondition'] ? 'border-red-500' : ''} data-testid="temperature-input" /></FormField>
                <FormField label="CR (Condensation Resistance)" error={errors['thermalPerformance.cr']}><NumericInput placeholder="e.g., 45" value={form.thermalPerformance.cr} onChange={(v) => updateNested('thermalPerformance', 'cr', v)} className={errors['thermalPerformance.cr'] ? 'border-red-500' : ''} data-testid="ti-cr-input" /></FormField>
              </div>
            </div>
          )}

          {/* === STEP 5: Fire, Smoke & Code Compliance === */}
          {step === 5 && (
            <div className="space-y-4" data-testid="wizard-step-5">
              <h4 className="font-semibold text-sm border-b pb-2">Fire, Smoke & Code Compliance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Fire Rating" error={errors['fireCompliance.fireRating']}><FormSelect value={form.fireCompliance.fireRating} onValueChange={(v) => updateNested('fireCompliance', 'fireRating', v)} placeholder="Select fire rating..." options={FIRE_RATINGS} error={errors['fireCompliance.fireRating']} testId="fire-rating-select" /></FormField>
                <FormField label="ASTM E84" required={!isFireRatingNone} error={errors['fireCompliance.astmE84']}><FormSelect value={form.fireCompliance.astmE84} onValueChange={(v) => updateNested('fireCompliance', 'astmE84', v)} placeholder="Select ASTM E84..." options={ASTM_E84_OPTIONS} disabled={isFireRatingNone} error={errors['fireCompliance.astmE84']} testId="astm-e84-select" /></FormField>
                <FormField label="NFPA / UL Assembly Reference" required={!isFireRatingNone} error={errors['fireCompliance.nfpaReference']}><Input placeholder="e.g., NFPA 285" value={form.fireCompliance.nfpaReference} onChange={(e) => updateNested('fireCompliance', 'nfpaReference', e.target.value)} disabled={isFireRatingNone} className={`${isFireRatingNone ? 'bg-slate-100 cursor-not-allowed' : ''} ${errors['fireCompliance.nfpaReference'] ? 'border-red-500' : ''}`} data-testid="nfpa-reference-input" /></FormField>
                <FormField label="ICC-ES / Evaluation Report No"><Input placeholder="e.g., ESR-1647" value={form.fireCompliance.iccESReport} onChange={(e) => updateNested('fireCompliance', 'iccESReport', e.target.value)} disabled={isFireRatingNone} className={isFireRatingNone ? 'bg-slate-100 cursor-not-allowed' : ''} data-testid="icc-es-input" /></FormField>
              </div>
              {isFireRatingNone && <p className="text-xs text-slate-400">Fire Rating is set to None — other fields are disabled.</p>}
            </div>
          )}

          {/* === STEP 6: Moisture & Vapor Properties === */}
          {step === 6 && (
            <div className="space-y-4" data-testid="wizard-step-6">
              <h4 className="font-semibold text-sm border-b pb-2">Moisture & Vapor Properties</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Water Absorption (%)" error={errors['moistureProperties.waterAbsorption']}><NumericInput placeholder="e.g., 0.3" value={form.moistureProperties.waterAbsorption} onChange={(v) => updateNested('moistureProperties', 'waterAbsorption', v)} className={errors['moistureProperties.waterAbsorption'] ? 'border-red-500' : ''} data-testid="water-absorption-input" /></FormField>
                <FormField label="Vapor Permeance (perms)" error={errors['moistureProperties.vaporPermeance']}><NumericInput placeholder="e.g., 1.0" value={form.moistureProperties.vaporPermeance} onChange={(v) => updateNested('moistureProperties', 'vaporPermeance', v)} className={errors['moistureProperties.vaporPermeance'] ? 'border-red-500' : ''} data-testid="vapor-permeance-input" /></FormField>
                <FormField label="ASTM E96 Method"><FormSelect value={form.moistureProperties.astmE96Method} onValueChange={(v) => updateNested('moistureProperties', 'astmE96Method', v)} placeholder="Select method..." options={ASTM_E96_METHODS} testId="astm-e96-select" /></FormField>
              </div>
              <div className="space-y-1" data-testid="application-area-section">
                <MultiSelectCheckboxes label="Application Area *" options={APPLICATION_AREAS} selected={form.moistureProperties.applicationArea || []} onChange={(v) => { updateNested('moistureProperties', 'applicationArea', v); }} testId="application-area" />
                {errors['moistureProperties.applicationArea'] && <p className="text-xs text-red-500">{errors['moistureProperties.applicationArea']}</p>}
              </div>
            </div>
          )}

          {/* === STEP 7: Installation Parameters === */}
          {step === 7 && (
            <div className="space-y-4" data-testid="wizard-step-7">
              <h4 className="font-semibold text-sm border-b pb-2">Installation Parameters</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Attachment Method" required error={errors['installationParameters.attachmentMethod']}><FormSelect value={form.installationParameters.attachmentMethod} onValueChange={(v) => updateNested('installationParameters', 'attachmentMethod', v)} placeholder="Select attachment method..." options={ATTACHMENT_METHODS} error={errors['installationParameters.attachmentMethod']} testId="attachment-method-select" /></FormField>
                {form.installationParameters.attachmentMethod === 'Other' ? (
                  <FormField label="Specify Attachment Method" required error={errors['installationParameters.attachmentMethodOther']}><Input placeholder="Enter custom attachment method..." value={form.installationParameters.attachmentMethodOther} onChange={(e) => updateNested('installationParameters', 'attachmentMethodOther', e.target.value)} className={errors['installationParameters.attachmentMethodOther'] ? 'border-red-500' : ''} data-testid="attachment-method-other-input" /></FormField>
                ) : (<div />)}
                <FormField label="Substrate Type"><FormSelect value={form.installationParameters.substrateType} onValueChange={(v) => updateNested('installationParameters', 'substrateType', v)} placeholder="Select substrate type..." options={SUBSTRATE_TYPES} testId="substrate-type-select" /></FormField>
                <FormField label="Max Fastener Spacing"><Input placeholder="e.g., 24 in O.C." value={form.installationParameters.maxFastenerSpacing} onChange={(e) => updateNested('installationParameters', 'maxFastenerSpacing', e.target.value)} data-testid="fastener-spacing-input" /></FormField>
                <FormField label="Installation Temperature Range"><Input placeholder="e.g., 40°F - 100°F" value={form.installationParameters.installationTempRange} onChange={(e) => updateNested('installationParameters', 'installationTempRange', e.target.value)} data-testid="install-temp-input" /></FormField>
                <FormField label="UV Exposure Limit (days)" error={errors['installationParameters.uvExposureLimit']}><NumericInput placeholder={isBelowGradeOnly ? 'N/A (Below-grade)' : 'e.g., 90'} value={form.installationParameters.uvExposureLimit} onChange={(v) => updateNested('installationParameters', 'uvExposureLimit', v)} disabled={isBelowGradeOnly} className={`${isBelowGradeOnly ? 'bg-slate-100 cursor-not-allowed' : ''} ${errors['installationParameters.uvExposureLimit'] ? 'border-red-500' : ''}`} data-testid="uv-exposure-input" />{isBelowGradeOnly && <p className="text-[10px] text-slate-400 mt-0.5">Disabled: Below-grade is the only Application Area</p>}</FormField>
              </div>
            </div>
          )}

          {/* === STEP 8: Interface & Responsibility === */}
          {step === 8 && (
            <div className="space-y-4" data-testid="wizard-step-8">
              <h4 className="font-semibold text-sm border-b pb-2">Interface & Responsibility</h4>
              <div className="space-y-4">
                <MultiSelectCheckboxes label="Adjacent Systems" options={ADJACENT_SYSTEMS} selected={form.interfaceResponsibility.adjacentSystems} onChange={(v) => updateNested('interfaceResponsibility', 'adjacentSystems', v)} testId="adjacent-systems" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Interface Responsibility" required error={errors['interfaceResponsibility.interfaceResponsibility']}><FormSelect value={form.interfaceResponsibility.interfaceResponsibility} onValueChange={(v) => { updateNested('interfaceResponsibility', 'interfaceResponsibility', v); if (v !== 'Other') updateNested('interfaceResponsibility', 'otherSubcontractor', ''); }} placeholder="Select responsibility..." options={INTERFACE_RESP_OPTIONS} error={errors['interfaceResponsibility.interfaceResponsibility']} testId="interface-resp-select" /></FormField>
                  {form.interfaceResponsibility.interfaceResponsibility === 'Other' ? (
                    <FormField label="Other Subcontractor" required error={errors['interfaceResponsibility.otherSubcontractor']}><Input placeholder="Subcontractor name..." value={form.interfaceResponsibility.otherSubcontractor} onChange={(e) => updateNested('interfaceResponsibility', 'otherSubcontractor', e.target.value)} className={errors['interfaceResponsibility.otherSubcontractor'] ? 'border-red-500' : ''} data-testid="other-subcontractor-input" /></FormField>
                  ) : (<div />)}
                  <FormField label="Transition Membrane Required?" required error={errors['interfaceResponsibility.transitionMembraneRequired']}><FormSelect value={form.interfaceResponsibility.transitionMembraneRequired} onValueChange={(v) => updateNested('interfaceResponsibility', 'transitionMembraneRequired', v)} placeholder="Select..." options={TRANSITION_MEMBRANE_OPTIONS} error={errors['interfaceResponsibility.transitionMembraneRequired']} testId="transition-membrane-select" /></FormField>
                </div>
              </div>
            </div>
          )}

          {/* === STEP 9: Submittals === */}
          {step === 9 && (
              <WizardSubmittalsStep
                displayList={submittals.displayList}
                onAddUpload={submittals.addUpload}
                onRemoveItem={submittals.removeItem}
                onUndoDelete={submittals.undoDelete}
                onPreview={handlePreviewSubmittal}
                docTypes={DOCUMENT_TYPES}
              />
          )}
          <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />

          {/* === STEP 10: Field Verification === */}
          {step === 10 && (() => {
            const isNonConformance = form.fieldVerification.nonConformance === 'Yes';
            const photos = form.fieldVerification.installationPhotos || [];
            return (
              <div className="space-y-4" data-testid="wizard-step-10">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Field Verification <span className="text-xs text-slate-400 font-normal">(Optional)</span></h4>

                {/* Row 1: Installed Thickness + Non-conformance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Installed Thickness (in)" tooltip="Thickness measured on site after installation">
                    <Input
                      placeholder="e.g., 2, 1.5, 1/2, 1 1/2"
                      value={form.fieldVerification.installedThickness}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^[\d./ ]*$/.test(v))
                          setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installedThickness: v } }));
                      }}
                      data-testid="fv-installed-thickness"
                    />
                  </FormField>
                  <FormField label="Non-conformance detected">
                    <div className="flex gap-2" data-testid="fv-nonconformance-toggle">
                      {['Yes', 'No'].map(val => {
                        const selected = form.fieldVerification.nonConformance === val;
                        const colorClass = val === 'Yes'
                          ? (selected ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' : 'hover:bg-red-50 hover:border-red-300')
                          : (selected ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:border-green-300');
                        return (
                          <Badge key={val} variant="outline"
                            className={`cursor-pointer transition-colors px-4 py-1.5 text-sm ${colorClass}`}
                            onClick={() => setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, nonConformance: prev.fieldVerification.nonConformance === val ? '' : val } }))}
                            data-testid={`fv-nc-${val.toLowerCase()}`}
                          >{val}</Badge>
                        );
                      })}
                    </div>
                  </FormField>
                </div>

                {/* Row 2: Inspector Comments (full width) */}
                <FormField label={`Inspector Comments${isNonConformance ? ' *' : ''}`} error={errors['fieldVerification.inspectorComments']}>
                  <Textarea
                    placeholder="Enter field inspection notes"
                    maxLength={1000}
                    rows={4}
                    value={form.fieldVerification.inspectorComments}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, inspectorComments: e.target.value } }));
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'fieldVerification.inspectorComments': null }));
                    }}
                    className={errors['fieldVerification.inspectorComments'] ? 'border-red-500' : ''}
                    data-testid="fv-inspector-comments"
                  />
                  <p className="text-[10px] text-slate-400 text-right mt-0.5">{(form.fieldVerification.inspectorComments || '').length}/1000</p>
                </FormField>

                {/* Row 3: Installation Photos (full width) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-sm font-medium">Installation Photos <span className="text-slate-400 font-normal">(max 20 photos)</span></Label>
                    {photos.length < 20 && !photoUploading && (
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-medium text-slate-600 hover:bg-orange-500 hover:text-white border rounded-md px-2.5 py-1 hover:border-orange-500 transition-colors" data-testid="fv-upload-photo-area">
                        <Upload className="h-3.5 w-3.5" />Upload Photo
                        <input type="file" accept="image/jpeg,image/png" multiple className="hidden" data-testid="fv-photo-input" onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          const remaining = 20 - photos.length;
                          const toUpload = files.slice(0, remaining);
                          if (files.length > remaining) toast.error(`Maximum 20 photos allowed. Only first ${remaining} will be uploaded.`);
                          setPhotoUploading(true);
                          try {
                            for (const f of toUpload) {
                              if (!['image/jpeg', 'image/png'].includes(f.type)) { toast.error(`${f.name}: Only JPG and PNG allowed`); continue; }
                              const fd = new FormData();
                              fd.append('file', f);
                              const res = await axios.post(`${API}/projects/${projectId}/field-verification/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              const photo = res.data;
                              setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installationPhotos: [...(prev.fieldVerification.installationPhotos || []), { url: photo.url, publicId: photo.publicId, fileName: photo.fileName, uploadedAt: photo.uploadedAt, takenAt: f.lastModified ? new Date(f.lastModified).toISOString() : null }] } }));
                            }
                            toast.success('Photo(s) uploaded');
                          } catch { toast.error('Photo upload failed'); }
                          finally { setPhotoUploading(false); e.target.value = ''; }
                        }} />
                      </label>
                    )}
                  </div>
                  {photoUploading && (
                    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <p className="text-sm font-medium text-blue-800">Uploading photo...</p>
                    </div>
                  )}
                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="fv-photo-grid">
                      {photos.map((photo, i) => (
                        <div key={i} className="relative group border rounded-lg overflow-hidden bg-slate-50 cursor-pointer" data-testid={`fv-photo-${i}`}
                          onClick={() => setViewerPhotoIndex(i)}>
                          <img src={photo.url} alt={photo.fileName} className="w-full h-24 object-cover" />
                          <button
                            type="button"
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installationPhotos: prev.fieldVerification.installationPhotos.filter((_, j) => j !== i) } })); }}
                            data-testid={`fv-remove-photo-${i}`}
                          ><X className="h-3 w-3" /></button>
                          <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-0.5">
                            <p className="text-[9px] text-white truncate">{photo.fileName}</p>
                            <p className="text-[8px] text-white/70">{photo.uploadedAt ? new Date(photo.uploadedAt).toLocaleDateString() : ''}{photo.takenAt ? ` | Taken: ${new Date(photo.takenAt).toLocaleDateString()}` : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {photos.length === 0 && !photoUploading && <p className="text-xs text-slate-400">No photos uploaded yet</p>}
                  {photos.length >= 20 && <p className="text-xs text-slate-400 mt-1">Maximum 20 photos reached</p>}
                </div>
              </div>
            );
          })()}

          {/* === STEP 11: Review Summary === */}
          {isReviewStep && (
            <div className="space-y-1" data-testid="wizard-step-11">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Review Summary</h4>
              <div className="bg-slate-50 rounded-lg p-4 divide-y divide-slate-200">
                <ReviewProductHeader
                  imagePreview={form.imagePreview}
                  qrToken={form.qrToken}
                  infoRows={[
                    ['Product Name', form.productNameModel],
                    ['Manufacturer', selectedManufacturer?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                  ]}
                />
                <div className="pt-3">
                  <ReviewSection title="Insulation Types" rows={[
                    ['Total Selected', totalSelected > 0 ? `${totalSelected} type(s)` : null],
                    ...selectedGroupsForPanel.map(sg => [sg.group, sg.items.join(', ')]),
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Material & Physical Properties" rows={[
                    ['Material Type', form.materialProperties.materialType === 'Other' ? `Other: ${form.materialProperties.materialTypeOther}` : form.materialProperties.materialType],
                    ['Thickness', form.materialProperties.thickness ? `${form.materialProperties.thickness} in` : null],
                    ['Density', form.materialProperties.density],
                    ['Compressive Strength', form.materialProperties.compressiveStrength ? `${form.materialProperties.compressiveStrength} psi` : null],
                    ['Facing Type', form.materialProperties.facingType],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Thermal Performance" rows={[
                    ['R-Value', form.thermalPerformance.declaredRValue],
                    ['U-Value', form.thermalPerformance.calculatedUValue],
                    ['Test Standard', form.thermalPerformance.testStandard],
                    ['Temperature Condition', form.thermalPerformance.temperatureCondition ? `${form.thermalPerformance.temperatureCondition} °F` : null],
                    ['CR', form.thermalPerformance.cr],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Fire, Smoke & Code Compliance" rows={[
                    ['Fire Rating', form.fireCompliance.fireRating],
                    ['ASTM E84', form.fireCompliance.astmE84],
                    ['NFPA / UL Reference', form.fireCompliance.nfpaReference],
                    ['ICC-ES Report', form.fireCompliance.iccESReport],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Moisture & Vapor Properties" rows={[
                    ['Water Absorption', form.moistureProperties.waterAbsorption ? `${form.moistureProperties.waterAbsorption}%` : null],
                    ['Vapor Permeance', form.moistureProperties.vaporPermeance ? `${form.moistureProperties.vaporPermeance} perms` : null],
                    ['ASTM E96 Method', form.moistureProperties.astmE96Method],
                    ['Application Area', form.moistureProperties.applicationArea?.join(', ')],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Installation Parameters" rows={[
                    ['Attachment Method', form.installationParameters.attachmentMethod === 'Other' ? `Other: ${form.installationParameters.attachmentMethodOther}` : form.installationParameters.attachmentMethod],
                    ['Substrate Type', form.installationParameters.substrateType],
                    ['Max Fastener Spacing', form.installationParameters.maxFastenerSpacing],
                    ['Install Temp Range', form.installationParameters.installationTempRange],
                    ['UV Exposure Limit', isBelowGradeOnly ? 'N/A (Below-grade)' : (form.installationParameters.uvExposureLimit ? `${form.installationParameters.uvExposureLimit} days` : null)],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Interface & Responsibility" rows={[
                    ['Adjacent Systems', form.interfaceResponsibility.adjacentSystems?.join(', ')],
                    ['Interface Responsibility', form.interfaceResponsibility.interfaceResponsibility],
                    ...(form.interfaceResponsibility.interfaceResponsibility === 'Other' ? [['Other Subcontractor', form.interfaceResponsibility.otherSubcontractor]] : []),
                    ['Transition Membrane', form.interfaceResponsibility.transitionMembraneRequired],
                  ]} />
                </div>
                <WizardSubmittalsReview displayList={submittals.displayList} docTypes={DOCUMENT_TYPES} />
                <div className="pt-3">
                  <ReviewSection title="Field Verification" rows={[
                    ['Installed Thickness', form.fieldVerification.installedThickness ? `${form.fieldVerification.installedThickness} in` : null],
                    ['Non-conformance', form.fieldVerification.nonConformance ? (() => {
                      const v = form.fieldVerification.nonConformance;
                      if (v === 'Yes') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-500 text-white text-xs">Yes</span>;
                      if (v === 'No') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600 text-white text-xs">No</span>;
                      return v;
                    })() : null],
                    ['Inspector Comments', form.fieldVerification.inspectorComments || null],
                    ['Installation Photos', form.fieldVerification.installationPhotos?.length ? `${form.fieldVerification.installationPhotos.length} photo(s)` : null],
                  ]} />
                  {form.fieldVerification.installationPhotos?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {form.fieldVerification.installationPhotos.map((p, i) => (
                        <img key={i} src={p.url} alt={p.fileName} className="w-16 h-16 object-cover rounded border" data-testid={`review-fv-photo-${i}`} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="pt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Status after save:</span>
                    <span className="font-semibold text-green-600">Active</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" onClick={requestClose} data-testid="wizard-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving || finalizing || !isDirty} data-testid="save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save as Draft
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBack} disabled={step === 1} data-testid="wizard-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
            {isReviewStep ? (
              <Button onClick={handleFinalSave} disabled={finalizing || saving} data-testid="final-save-btn">
                {finalizing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? 'Save' : 'Create Product'}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={saving} data-testid="wizard-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            )}
          </div>
        </div>
        {/* Photo Viewer Modal — rendered at DialogContent level for full coverage */}
        {viewerPhotoIndex !== null && (form.fieldVerification.installationPhotos || [])[viewerPhotoIndex] && (
          <PhotoViewerModal
            photos={form.fieldVerification.installationPhotos}
            currentIndex={viewerPhotoIndex}
            onIndexChange={setViewerPhotoIndex}
            onClose={() => setViewerPhotoIndex(null)}
          />
        )}
      </DialogContent>

      <ClipboardPasteModal isOpen={showPasteModal} onClose={() => setShowPasteModal(false)} onUpload={handlePasteUpload} title="Paste Product Image" uploading={pasteUploading} />
      <ConfirmDialog open={showCloseConfirm} title="Unsaved Changes" description="You have unsaved changes. Are you sure you want to close? Your changes will be lost." confirmText="Close" variant="destructive" onConfirm={resetAndClose} onClose={() => setShowCloseConfirm(false)} />
    </Dialog>
  );
};

export default AddProductWizard;
